<div class="status-page container">
  <div class="status-header">
    <div class="status-header-content">
      <h1 class="headline mt-primary-text">System Status</h1>
      <p class="subheadline">Live readiness from the MemberTrace public health endpoint</p>
    </div>
    <div class="status-overview" *ngIf="statusData">
      <div class="overall-status" [ngClass]="getStatusClass(statusData.overall.status)">
        <div class="status-indicator">
          <span class="status-icon">{{ getStatusIcon(statusData.overall.status) }}</span>
          <span class="status-label">{{ statusData.overall.status | titlecase }}</span>
        </div>
      </div>
    </div>
  </div>

  <div class="loading-container" *ngIf="loading && !statusData">
    <div class="spinner"></div>
    <p>Loading status information...</p>
  </div>

  <div class="error-container" *ngIf="error">
    <div class="error-message">
      <span class="error-icon">!</span>
      <p>{{ error }}</p>
      <button class="retry-button" (click)="loadStatus()">Retry</button>
    </div>
  </div>

  <div class="status-content" *ngIf="statusData && !loading">
    <section class="uptime-section">
      <h2 class="section-title">Live Health Snapshot</h2>
      <div class="uptime-cards">
        <div class="uptime-card">
          <div class="uptime-value">{{ statusData.meta.version || 'Unknown' }}</div>
          <div class="uptime-label">API Version</div>
        </div>
        <div class="uptime-card">
          <div class="uptime-value">{{ formatDuration(statusData.meta.uptimeSeconds) }}</div>
          <div class="uptime-label">Process Uptime</div>
        </div>
        <div class="uptime-card">
          <div class="uptime-value">{{ formatMilliseconds(statusData.meta.responseTimeMs) }}</div>
          <div class="uptime-label">Probe Duration</div>
        </div>
        <div class="uptime-card">
          <div class="uptime-value">{{ formatRelativeTime(statusData.overall.lastChecked) }}</div>
          <div class="uptime-label">Last Checked</div>
        </div>
      </div>
      <div class="healthcheck-stats">
        <div class="stat-item">
          <span class="stat-label">Components Checked</span>
          <span class="stat-value">{{ statusData.overall.totalChecks | number }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Operational</span>
          <span class="stat-value success">{{ statusData.overall.successfulChecks | number }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Impacted</span>
          <span class="stat-value failed">{{ statusData.overall.failedChecks | number }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Source</span>
          <span class="stat-value">{{ statusData.meta.sourceLabel }}</span>
        </div>
      </div>
      <p class="snapshot-note" *ngIf="!statusData.meta.historyAvailable">
        This page reflects the current public readiness probe. Uptime history is temporarily unavailable.
      </p>
    </section>

    <section class="history-section" *ngIf="statusData.meta.historyAvailable">
      <h2 class="section-title">Uptime History</h2>
      <div class="uptime-cards">
        <div class="uptime-card">
          <div class="uptime-value">{{ formatPercent(statusData.historicalUptime.last24Hours) }}</div>
          <div class="uptime-label">Last 24 Hours</div>
        </div>
        <div class="uptime-card">
          <div class="uptime-value">{{ formatPercent(statusData.historicalUptime.last7Days) }}</div>
          <div class="uptime-label">Last 7 Days</div>
        </div>
        <div class="uptime-card">
          <div class="uptime-value">{{ formatPercent(statusData.historicalUptime.last30Days) }}</div>
          <div class="uptime-label">Last 30 Days</div>
        </div>
        <div class="uptime-card">
          <div class="uptime-value">{{ formatPercent(statusData.historicalUptime.last90Days) }}</div>
          <div class="uptime-label">Last 90 Days</div>
        </div>
      </div>
      <div class="uptime-bars" *ngIf="statusData.dailyUptime.length > 0" role="img"
           [attr.aria-label]="'Daily API uptime for the last ' + statusData.dailyUptime.length + ' days'">
        <span class="uptime-bar" *ngFor="let day of statusData.dailyUptime; trackBy: trackByDate"
              [ngClass]="getUptimeBarClass(day)" [title]="getUptimeBarTitle(day)"></span>
      </div>
      <div class="uptime-bars-legend" *ngIf="statusData.dailyUptime.length > 0">
        <span>{{ statusData.dailyUptime.length }} days ago</span>
        <span class="legend-keys">
          <span class="legend-key"><span class="uptime-bar bar-up"></span>Operational</span>
          <span class="legend-key"><span class="uptime-bar bar-minor"></span>Minor</span>
          <span class="legend-key"><span class="uptime-bar bar-major"></span>Outage</span>
          <span class="legend-key"><span class="uptime-bar bar-nodata"></span>No data</span>
        </span>
        <span>Today</span>
      </div>
      <p class="snapshot-note">
        Uptime is measured by an external health check of the MemberTrace API (UTC days)<span *ngIf="statusData.meta.trackingSince">, tracked since {{ formatDate(statusData.meta.trackingSince) }}</span>.
        <span *ngIf="statusData.meta.historyGeneratedAt">History updated {{ formatRelativeTime(statusData.meta.historyGeneratedAt) }}.</span>
      </p>
    </section>

    <section class="components-section">
      <h2 class="section-title">Component Status</h2>
      <div class="components-grid">
        <div class="component-card" *ngFor="let component of statusData.overall.components" [ngClass]="getStatusClass(component.status)">
          <div class="component-header">
            <div class="component-status">
              <span class="status-icon-small">{{ getStatusIcon(component.status) }}</span>
              <span class="component-name">{{ component.name }}</span>
            </div>
            <span class="status-badge" [ngClass]="getStatusClass(component.status)">
              {{ component.status | titlecase }}
            </span>
          </div>
          <div class="component-body" *ngIf="component.description">
            <p class="component-description">{{ component.description }}</p>
          </div>
          <div class="component-footer">
            <span class="last-checked">Last checked: {{ formatRelativeTime(component.lastChecked) }}</span>
          </div>
        </div>
      </div>
    </section>

    <section class="alerts-section" *ngIf="statusData.activeAlerts.length > 0">
      <h2 class="section-title">Active Alerts</h2>
      <div class="alerts-list">
        <div class="alert-item" *ngFor="let alert of statusData.activeAlerts" [ngClass]="getSeverityClass(alert.severity)">
          <div class="alert-header">
            <span class="alert-severity">{{ alert.severity | titlecase }}</span>
            <span class="alert-time">{{ formatRelativeTime(alert.timestamp) }}</span>
          </div>
          <div class="alert-body">
            <h3 class="alert-title">{{ alert.title }}</h3>
            <p class="alert-message">{{ alert.message }}</p>
            <div class="alert-meta">
              <span class="alert-source">Source: {{ alert.source }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="incidents-section">
      <h2 class="section-title">Incident History</h2>
      <div class="incidents-list" *ngIf="statusData.recentIncidents.length > 0; else noIncidents">
        <div class="incident-item" *ngFor="let incident of statusData.recentIncidents" [ngClass]="getSeverityClass(incident.severity)">
          <div class="incident-header">
            <div class="incident-title-row">
              <h3 class="incident-title">{{ incident.title }}</h3>
              <span class="incident-status">{{ incident.status | titlecase }}</span>
            </div>
            <div class="incident-meta">
              <span class="incident-severity" [ngClass]="getSeverityClass(incident.severity)">
                {{ incident.severity | titlecase }}
              </span>
              <span class="incident-time">
                Started: {{ formatDate(incident.startTime) }}
                <span *ngIf="incident.endTime"> | Resolved: {{ formatDate(incident.endTime) }}</span>
                <span *ngIf="incident.durationSeconds != null"> | Duration: {{ formatDuration(incident.durationSeconds) }}</span>
              </span>
            </div>
          </div>
          <div class="incident-body">
            <p class="incident-description">{{ incident.description }}</p>
            <div class="affected-components" *ngIf="incident.affectedComponents.length > 0">
              <span class="affected-label">Affected:</span>
              <span class="component-tag" *ngFor="let comp of incident.affectedComponents">{{ comp }}</span>
            </div>
            <div class="affected-components" *ngIf="incident.checks?.length">
              <span class="affected-label">Detected by:</span>
              <span class="component-tag" *ngFor="let check of incident.checks">{{ check }}</span>
            </div>
          </div>
        </div>
      </div>
      <ng-template #noIncidents>
        <div class="no-incidents">
          <span class="no-incidents-icon">{{ getStatusIcon(SystemStatus.OPERATIONAL) }}</span>
          <p>No incidents in the last 90 days. Current issues appear above as live alerts.</p>
        </div>
      </ng-template>
    </section>
  </div>

  <div class="status-footer" *ngIf="statusData">
    <p class="footer-note">
      Status page updates every {{ pollInterval / 1000 }} seconds from the public API health endpoint and published uptime history.
      For more information or to report an issue, please <a routerLink="/contact">contact us</a>.
    </p>
  </div>
</div>
